import { useApolloClient } from '@apollo/client/react/hooks';
import { revertConflictingFields } from '@oolio-group/client-utils';
import {
  Order,
  OrderAction,
  OrderEvent,
  OrderStatus,
} from '@oolio-group/domain';
import { encodeHlc, getHlc } from '@oolio-group/order-helper';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { logCdsEvent, logCdsEventOnce } from '../../actionLogging/cdsAudit';
import { orderEventsHandler } from '../../utils/orderEventsHandler';
import { isForeignLoneInitiate } from '../../utils/isForeignLoneInitiate';
import { useCallback, useEffect, useRef, useState } from 'react';
import { concatMap, from, observeOn, queueScheduler } from 'rxjs';
import { sessionSubject } from '../../state/sessionObservable';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { cdsOrderEventsSubject } from './cdsOrderEventsObservable';
import {
  BaseEventEmitter,
  subscribeOrderEventsStream,
} from '../../utils/localComms';
import { CDS_ORDER_EVENT } from '../../graphql/subscription';
import { localCommsSubject } from '../../state/localComms';
import { upsellComboStore } from '../../store/upsellCombo';
import { paidAmountByRequest } from '../../utils/paidAmountByRequest';
import Sentry from '../../utils/Sentry/Sentry';
import { tap2PayEmbeddedManager } from '../../utils/Tap2PayEmbeddedManager';
import {
  EMBEDDED_ORDER_TOPIC,
  EmbeddedOrderMessage,
  isEmbedded,
  parseEmbeddedOrderMessage,
} from '../../embedded/embedded';

const MAX_RESUBSCRIBE_ATTEMPTS = 5;
const RESUBSCRIBE_BASE_DELAY_MS = 1000;
const RESUBSCRIBE_MAX_DELAY_MS = 30000;

/**
 * The order snapshot is recomputed on every inbound event batch, so anything
 * logged from here is keyed on the value it reports rather than the fact that
 * it was observed. Item-level changes are deliberately not logged at all — the
 * order summary on ORDER_CLOSED covers what was actually bought.
 */
const logOrderChanges = (previous: Order, next: Order): void => {
  logCdsEventOnce(`ORDER_STARTED:${next.id}`, {
    actionType: 'ORDER_STARTED',
    tag: AuditLogTags.EVENT,
    phrase: 'order shown on customer display',
    orderId: next.id,
    orderNumber: next.orderNumber,
    context: { orderType: next.orderType?.name },
  });

  const customerId = next.customer?.id;
  if (customerId && customerId !== previous.customer?.id) {
    logCdsEvent({
      actionType: 'LOYALTY_CUSTOMER_ASSIGNED',
      tag: AuditLogTags.USER_ACTION,
      phrase: 'customer attached to order',
      orderId: next.id,
      orderNumber: next.orderNumber,
      context: { customerId, loyaltyMember: next.customer?.loyaltyMember },
    });
  }

  const rewardDiscount = next.rewardDiscountAmount ?? 0;
  if (rewardDiscount !== (previous.rewardDiscountAmount ?? 0)) {
    const applied = rewardDiscount > 0;
    logCdsEvent({
      actionType: applied ? 'LOYALTY_REWARD_APPLIED' : 'LOYALTY_REWARD_REMOVED',
      tag: AuditLogTags.USER_ACTION,
      phrase: applied ? 'loyalty reward applied' : 'loyalty reward removed',
      orderId: next.id,
      orderNumber: next.orderNumber,
      context: {
        rewardDiscountAmount: rewardDiscount,
        isLoyaltyApplied: next.isLoyaltyApplied,
      },
    });
  }
};

const recordPaidAmounts = (order: Order): void => {
  order.payments?.forEach(payment => {
    if (payment.paymentRequestId && payment.tendered) {
      paidAmountByRequest.set(payment.paymentRequestId, payment.tendered);
    }
  });
};

const observeRemoteHlc = (events: OrderEvent[]): void => {
  const maxHlc = events.reduce<number>(
    (acc, e) => Math.max(acc, e.hlc ?? encodeHlc(e.timestamp, 0)),
    0,
  );
  if (maxHlc > 0) {
    getHlc().receive(maxHlc);
  }
};

const logOrderClosed = (closedOrder: Order): void => {
  if (!closedOrder.id) return;
  logCdsEventOnce(`ORDER_CLOSED:${closedOrder.id}`, {
    actionType: 'ORDER_CLOSED',
    tag: AuditLogTags.EVENT,
    phrase: 'order closed',
    orderId: closedOrder.id,
    orderNumber: closedOrder.orderNumber,
    context: {
      itemCount: closedOrder.orderItems?.length ?? 0,
      total: closedOrder.totalPrice,
    },
  });
};

const describeEvents = (events: OrderEvent[]) =>
  events.map(event => ({
    id: event.id,
    action: event.action,
    orderId: event.orderId,
    keys: Object.keys(event),
  }));

const useCartEvent = () => {
  const [order, setOrder] = useState<Order>({} as Order);
  // used to calculate new order data based. changing preOrder won't make component rerender
  const preOrder = useRef<Order>({} as Order);
  const orderEvents = useRef<OrderEvent[]>([]);
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const client = useApolloClient();
  const { value: isLocalCommsEligible } =
    useBehaviorSubjectState(localCommsSubject);

  const onOrderEventsReceived = useCallback(async (events: OrderEvent[]) => {
    if (!events.length) return;
    // Advance the local HLC past any incoming remote observations so
    // subsequent local events are ordered strictly after what we just saw.
    observeRemoteHlc(events);
    let eventArray = revertConflictingFields(events);
    const isCloseOrder = eventArray.some(
      event => event.action === OrderAction.ORDER_CLOSE,
    );
    if (isCloseOrder) {
      const closedOrder = preOrder.current;
      // a batch can carry the final payment together with ORDER_CLOSE
      const closingOrderId = eventArray.find(
        event => event.action === OrderAction.ORDER_CLOSE,
      )?.orderId;
      const closingEvents = eventArray.filter(
        event =>
          event.action !== OrderAction.ORDER_CLOSE &&
          event.orderId === closingOrderId,
      );
      if (closingEvents.length) {
        const events = orderEvents.current
          .filter(event => event.orderId === closingOrderId)
          .concat(closingEvents);
        try {
          recordPaidAmounts(await orderEventsHandler(events, {} as Order));
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              context: 'cds-order-events-compute-on-close',
              orderId: closingOrderId,
              incomingEvents: describeEvents(eventArray),
            },
          });
        }
      }
      logOrderClosed(closedOrder);
      preOrder.current = {} as Order;
      setOrder({} as Order);
      upsellComboStore.setState(eventArray);
      orderEvents.current = [];
      return;
    }

    const isSameOrder = eventArray.every(
      event => event.orderId === preOrder.current.id,
    );

    // A lone ORDER_INITIATE for a *different* order (e.g. another table being
    // started on the POS) must not be merged into the current order's event
    // stream. Mixing order ids makes orderUpdateEventsValidation reject the
    // whole accumulated batch, which permanently wipes the reduced order (and
    // with it the ability to auto-launch a re-attempted payment on a recalled
    // order). Ignore it while an order is already on screen.
    if (isForeignLoneInitiate(preOrder.current.id, eventArray)) {
      logCdsEventOnce(
        `ORDER_EVENTS_DISCARDED:foreign-initiate:${eventArray[0].orderId}`,
        {
          actionType: 'ORDER_EVENTS_DISCARDED',
          tag: AuditLogTags.EVENT,
          phrase: 'ignored a different order starting on the POS',
          orderId: preOrder.current.id,
          orderNumber: preOrder.current.orderNumber,
          level: 'debug',
          context: {
            reason: 'FOREIGN_LONE_INITIATE',
            incomingOrderId: eventArray[0].orderId,
          },
        },
      );
      return;
    }

    if (
      !isSameOrder &&
      !(
        eventArray.length === 1 &&
        eventArray[0].action === OrderAction.ORDER_INITIATE
      )
    ) {
      const lastOrderId = eventArray[eventArray.length - 1].orderId;
      eventArray = eventArray.filter(event => event.orderId === lastOrderId);
      orderEvents.current = [];
    }

    const previousEvents = orderEvents.current;
    orderEvents.current = previousEvents.concat(eventArray);
    let newOrder: Order;
    try {
      newOrder = await orderEventsHandler(orderEvents.current, {} as Order);
    } catch (error) {
      // The buffer is replayed in full each time; keeping a batch that throws would fail every later one
      orderEvents.current = previousEvents;
      Sentry.captureException(error, {
        extra: {
          context: 'cds-order-events-compute',
          orderId: eventArray[0]?.orderId,
          incomingEvents: describeEvents(eventArray),
        },
      });
      return;
    }
    if (newOrder.id && newOrder.status !== OrderStatus.VOID) {
      if (newOrder.id !== preOrder.current.id) {
        void tap2PayEmbeddedManager.warmup();
      }
      recordPaidAmounts(newOrder);
      logOrderChanges(preOrder.current, newOrder);
      setOrder(newOrder);
      upsellComboStore.setState(eventArray);
      preOrder.current = newOrder;
    }
  }, []);

  const onEmbeddedOrderReceived = useCallback(
    ({ order: nextOrder, events }: EmbeddedOrderMessage) => {
      const shownId = preOrder.current.id;
      if (isForeignLoneInitiate(shownId, events)) return;
      if (!events.length && shownId && nextOrder?.id !== shownId) return;
      observeRemoteHlc(events);
      if (nextOrder) recordPaidAmounts(nextOrder);
      const isCloseOrder = events.some(
        event => event.action === OrderAction.ORDER_CLOSE,
      );
      if (isCloseOrder || !nextOrder) {
        logOrderClosed(nextOrder ?? preOrder.current);
        preOrder.current = {} as Order;
        setOrder({} as Order);
        if (events.length) upsellComboStore.setState(events);
        return;
      }
      if (nextOrder.status === OrderStatus.VOID) return;
      logOrderChanges(preOrder.current, nextOrder);
      setOrder(nextOrder);
      preOrder.current = nextOrder;
      if (events.length) upsellComboStore.setState(events);
    },
    [],
  );

  useEffect(() => {
    if (!isEmbedded()) return;
    const emitter = new BaseEventEmitter(EMBEDDED_ORDER_TOPIC);
    emitter.addListener(data => {
      const message = parseEmbeddedOrderMessage(data);
      if (!message) return;
      onEmbeddedOrderReceived(message);
      if (message.events.length) cdsOrderEventsSubject.next(message.events);
    });
    void window.go?.main?.App?.SubscribeOrderEventsStream?.(
      EMBEDDED_ORDER_TOPIC,
    );
    return () => emitter.remove();
  }, [onEmbeddedOrderReceived]);

  useEffect(() => {
    if (isEmbedded()) return;
    const sub = cdsOrderEventsSubject
      .pipe(
        observeOn(queueScheduler),
        // one batch at a time: each replays and may roll back the shared buffer
        concatMap((events: OrderEvent[]) =>
          from(
            onOrderEventsReceived(events).catch(error => {
              Sentry.captureException(error, {
                extra: { context: 'cds-order-events-received' },
              });
            }),
          ),
        ),
      )
      .subscribe();

    return () => {
      sub.unsubscribe();
    };
  }, [onOrderEventsReceived]);
  useEffect(() => {
    let emitter: InstanceType<typeof BaseEventEmitter> | null = null;
    let subscription: { unsubscribe?: () => void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;

    if (isEmbedded()) return;
    if (isLocalCommsEligible === true) {
      // Local comms is the active source
      const subscribeToEvents = async () => {
        const topic = `orderEvents.store.${session?.currentStore?.id}.device.${session?.device?.id}`;
        emitter = await subscribeOrderEventsStream(topic);
        emitter?.addListener((eventsData: unknown) => {
          try {
            const payloadRaw: unknown =
              typeof eventsData === 'string'
                ? JSON.parse(eventsData)
                : eventsData;

            const payload = payloadRaw as { events?: string } | undefined;
            if (!payload?.events) return;

            const newEvents = JSON.parse(payload.events) as Array<OrderEvent>;
            cdsOrderEventsSubject.next(newEvents);
          } catch {
            // ignore malformed events
          }
        });
      };
      subscribeToEvents();
    } else {
      // Web subscription is the active source
      const cdsId = session?.customerDisplay?.id;
      const deviceId = session?.device?.id;
      // Both variables are String! server-side; wait for the session to populate them
      if (!cdsId || !deviceId) return;

      const scheduleResubscribe = () => {
        if (failures >= MAX_RESUBSCRIBE_ATTEMPTS) return;
        const delay = Math.min(
          RESUBSCRIBE_BASE_DELAY_MS * 2 ** failures,
          RESUBSCRIBE_MAX_DELAY_MS,
        );
        failures += 1;
        retryTimer = setTimeout(connect, delay);
      };

      const connect = () => {
        const observable = client.subscribe({
          query: CDS_ORDER_EVENT,
          variables: {
            cdsId,
            deviceId,
            lastActiveTime: 0,
            orderId: order.id,
          },
        });

        subscription = observable.subscribe({
          next({ data }) {
            failures = 0;
            if (data?.cdsOrderEvents) {
              cdsOrderEventsSubject.next(data?.cdsOrderEvents);
            }
          },
          error(err) {
            Sentry.captureException(err, {
              extra: {
                context: 'cds-order-event-subscription',
                orderId: order.id,
              },
            });
            logCdsEvent({
              actionType: 'ORDER_SUBSCRIPTION_ERROR',
              tag: AuditLogTags.SYSTEM,
              phrase: 'lost the order feed from POS',
              orderId: order.id,
              level: 'error',
              context: {
                errorMessage: err instanceof Error ? err.message : String(err),
              },
            });
            scheduleResubscribe();
          },
          complete() {
            Sentry.captureException(
              new Error('CDS order-event subscription closed'),
              {
                extra: {
                  context: 'cds-order-event-subscription',
                  orderId: order.id,
                },
              },
            );
            logCdsEvent({
              actionType: 'ORDER_SUBSCRIPTION_CLOSED',
              tag: AuditLogTags.SYSTEM,
              phrase: 'order feed from POS closed',
              orderId: order.id,
              level: 'warn',
            });
            scheduleResubscribe();
          },
        });
      };
      connect();
    }

    return () => {
      if (emitter instanceof BaseEventEmitter) {
        emitter.remove();
      }
      clearTimeout(retryTimer);
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [
    client,
    isLocalCommsEligible,
    order.id,
    session?.currentStore?.id,
    session.customerDisplay?.id,
    session?.device?.id,
  ]);
  return {
    order,
  };
};

export default useCartEvent;
